// Red Pawer (022-red-pawer): textos y normalización del perfil de red de una clínica
// Droopy. Las reglas de completitud las decide el backend; acá solo se traducen.

export const NETWORK_TYPE_OPTIONS = [
  { value: 'clinic', text: 'Clínica y centro' },
  { value: 'home_visit', text: 'A domicilio' }
];

const NETWORK_TYPE_LABELS = {
  clinic: 'Clínica y centro',
  home_visit: 'A domicilio'
};

const MISSING_FIELD_LABELS = {
  type: 'tipo',
  whatsapp: 'WhatsApp',
  address: 'dirección',
  commune: 'comuna',
  homeVisitCommunes: 'comunas de atención'
};

const CHILE_WHATSAPP = /^56\d{9}$/;

/** "Clínica y centro", "A domicilio" or "Sin definir" for a null/unknown type. */
export function networkTypeLabel(type) {
  return NETWORK_TYPE_LABELS[type] || 'Sin definir';
}

/** Spanish, comma-separated list of the missing fields the server returned. */
export function formatMissingFields(missing) {
  if (!Array.isArray(missing)) return '';
  return missing.map(field => MISSING_FIELD_LABELS[field] || field).join(', ');
}

/**
 * Normalizes a Chilean WhatsApp typed in any common form ("+56 9 1234 5678",
 * "9 1234 5678", "56912345678") to the stored digits "569XXXXXXXX".
 * Returns null for an empty value and the bare digits when they cannot be normalized,
 * so `isValidChileWhatsapp` rejects them.
 */
export function normalizeChileWhatsapp(value) {
  if (value === null || value === undefined) return null;
  const digits = String(value).replace(/\D/g, '');
  if (!digits) return null;
  if (digits.length === 9 && digits.startsWith('9')) return `56${digits}`;
  return digits;
}

/** True when the value is empty or normalizes to a Chilean mobile number. */
export function isValidChileWhatsapp(value) {
  const normalized = normalizeChileWhatsapp(value);
  return normalized === null || CHILE_WHATSAPP.test(normalized);
}

/** "56912345678" -> "+56 9 1234 5678"; anything else is returned as is. */
export function formatChileWhatsapp(digits) {
  if (!digits) return '';
  if (!CHILE_WHATSAPP.test(digits)) return digits;
  return `+56 ${digits.slice(2, 3)} ${digits.slice(3, 7)} ${digits.slice(7)}`;
}

/** Comparison key for a comuna name: lowercase, without accents or extra spaces. */
export function communeKey(name) {
  return String(name || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .trim()
    .toLowerCase();
}

/**
 * Builds the PUT body. Fields that do not apply to the chosen type are sent empty so a
 * type change does not leave stale data behind.
 */
export function toNetworkProfilePayload(form) {
  const isClinic = form.type === 'clinic';
  const address = isClinic && form.address ? form.address.trim() : '';
  return {
    type: form.type,
    whatsapp: normalizeChileWhatsapp(form.whatsapp),
    address: address || null,
    commune: isClinic && form.commune ? { code: form.commune.code, name: form.commune.name } : null,
    homeVisitCommunes: isClinic
      ? []
      : (form.homeVisitCommunes || []).map(c => ({ code: c.code, name: c.name })),
    openingHours: toOpeningHoursPayload(form.openingHours)
  };
}

// Horario de atención (opcional). Cada día va "Cerrado" o con hasta dos tramos elegidos
// de listas, para que el equipo lo registre siempre igual (decisión 2026-11-03).

export const OPENING_DAYS = [
  { key: 'mon', label: 'Lunes' },
  { key: 'tue', label: 'Martes' },
  { key: 'wed', label: 'Miércoles' },
  { key: 'thu', label: 'Jueves' },
  { key: 'fri', label: 'Viernes' },
  { key: 'sat', label: 'Sábado' },
  { key: 'sun', label: 'Domingo' }
];

export const MAX_OPENING_RANGES = 2;

/** "00:00", "00:30" … "23:30". */
export const OPENING_TIME_OPTIONS = Array.from({ length: 48 }, (_, i) => {
  const hours = String(Math.floor(i / 2)).padStart(2, '0');
  return `${hours}:${i % 2 ? '30' : '00'}`;
});

/** Range a day gets when it is opened, or a second range is added after the first. */
export function defaultOpeningRange(previous) {
  if (!previous) return { from: '09:00', to: '19:00' };
  const start = OPENING_TIME_OPTIONS.indexOf(previous.to);
  const from = OPENING_TIME_OPTIONS[Math.min(start + 2, 46)] || '15:00';
  const to = OPENING_TIME_OPTIONS[Math.min(OPENING_TIME_OPTIONS.indexOf(from) + 8, 47)];
  return { from, to };
}

/** Form model from the stored schedule (`null` means every day closed). */
export function toOpeningHoursForm(stored) {
  return OPENING_DAYS.map(day => {
    const ranges = (stored && Array.isArray(stored[day.key]) ? stored[day.key] : [])
      .slice(0, MAX_OPENING_RANGES)
      .map(range => ({ from: range.from, to: range.to }));
    return { key: day.key, label: day.label, closed: ranges.length === 0, ranges };
  });
}

/**
 * First problem of the schedule, in Spanish, or '' when it is valid: each range must end
 * after it starts, and the second range must start after the first one ends.
 */
export function openingHoursError(days) {
  for (const day of days || []) {
    if (day.closed) continue;
    const ranges = day.ranges || [];
    for (let i = 0; i < ranges.length; i++) {
      if (!(ranges[i].from < ranges[i].to)) {
        return `${day.label}: la hora de cierre debe ser después de la de apertura.`;
      }
      if (i > 0 && ranges[i].from < ranges[i - 1].to) {
        return `${day.label}: el segundo tramo debe empezar después de que termine el primero.`;
      }
    }
  }
  return '';
}

/** PUT value: `null` when every day is closed (not registered), else every day's ranges. */
export function toOpeningHoursPayload(days) {
  const open = (days || []).filter(day => !day.closed && day.ranges && day.ranges.length);
  if (!open.length) return null;
  return Object.fromEntries(
    (days || []).map(day => [
      day.key,
      day.closed ? [] : (day.ranges || []).map(range => ({ from: range.from, to: range.to }))
    ])
  );
}
